import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  MASS_OS_SERVICE_ORIGIN_LABEL,
  MASS_OS_SERVICE_TYPE_OPTIONS,
  massOsServiceOriginHintOf,
  MassOsItineraryService,
  MassOsServiceOrigin,
  MassOsTicket
} from 'src/entities/file';

@Component({
  selector: 'app-mass-os-itinerary-services',
  templateUrl: './mass-os-itinerary-services.component.html',
  styleUrls: ['./mass-os-itinerary-services.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MassOsItineraryServicesComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() servicios: MassOsItineraryService[] = [];
  /** Ids de servicio seleccionados para OS (estado vive en el padre). */
  @Input() selectedIds: string[] = [];
  /** Tickets seleccionados por servicio (serviceId → ticketIds), estado en el padre. */
  @Input() selectedTickets: Record<string, string[]> = {};
  /** Incremento del toolbar padre: expande todas las sub-tablas TICKET. */
  @Input() expandAllTickets = 0;

  @Output() toggleService = new EventEmitter<{
    serviceId: string;
    checked: boolean;
  }>();
  /** Checkbox de un ticket (crear OS por ticket), reenviado desde el expand. */
  @Output() ticketToggle = new EventEmitter<{
    serviceId: string;
    ticketId: string;
    checked: boolean;
  }>();

  dataSource = new MatTableDataSource<MassOsItineraryService>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  /** Patrón Ticketería: expand de servicios TICKET, un solo abierto a la vez. */
  readonly displayedColumns = [
    'expand',
    'Check',
    'origin',
    'serviceDay',
    'fecha',
    'tipoServicio',
    'nombreServicio',
    'nombre',
    'moneda',
    'pax',
    'personal',
    'costo'
  ];

  /** Mismos headers compactos que la grilla OS Masivas. */
  readonly headers = {
    serviceDay: 'DÍA',
    fecha: 'FECHA',
    tipoServicio: 'TIPO DE SERVICIO',
    nombreServicio: 'SERVICIO',
    nombre: 'PROVEEDOR',
    moneda: 'MONEDA',
    pax: 'PAX',
    personal: 'CANT. PERSONAL',
    costo: 'MONTO',
    origin: 'ORIGEN'
  };

  private expandedServiceIds = new Set<string>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.dataSource.data = this.servicios;
  }

  /** Reacciona al "Expandir todos" del toolbar del itinerario. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expandAllTickets'] && this.expandAllTickets > 0) {
      for (const svc of this.servicios) {
        if (this.isTicketService(svc)) {
          this.expandedServiceIds.add(svc.id);
        }
      }
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  trackByService = (_index: number, service: MassOsItineraryService): string =>
    service.id;

  /* ================= Paginación / orden ================= */

  onPageChange(): void {
    this.cdr.markForCheck();
  }

  /* ================= Selección de servicios ================= */

  isSelected(serviceId: string): boolean {
    return this.selectedIds.includes(serviceId);
  }

  /** Cantidad de tickets seleccionados dentro de un servicio TICKET. */
  ticketCountFor(serviceId: string): number {
    return (this.selectedTickets[serviceId] ?? []).length;
  }

  /** Indeterminado cuando el servicio TICKET tiene tickets internos marcados. */
  isServiceIndeterminate(service: MassOsItineraryService): boolean {
    return this.isTicketService(service) && this.ticketCountFor(service.id) > 0;
  }

  /** Checkbox del servicio TICKET oculto hasta expandir su sub-tabla de tickets.
   * Para TICKET también se exige tener tickets cargados (evita marcar "todos"
   * mientras el re-fetch aún no trae datos). */
  showServiceCheck(service: MassOsItineraryService): boolean {
    if (!this.isTicketService(service)) {
      return true;
    }
    return (
      this.isServiceExpanded(service) &&
      Array.isArray(service.tickets) &&
      service.tickets.length > 0
    );
  }

  get selectedCount(): number {
    return this.selectedIds.length;
  }

  paxOf(svc: MassOsItineraryService): number {
    return svc.paxQuantity ?? svc.pax ?? 0;
  }

  personalOf(svc: MassOsItineraryService): number {
    return svc.cantPersonal ?? 0;
  }

  /** Icono Tabler por tipo de servicio (mismos iconos del paso 1). */
  tipoIcon(tipo: string): string {
    const option = MASS_OS_SERVICE_TYPE_OPTIONS.find(
      (item) => item.label.toLowerCase() === tipo.toLowerCase()
    );
    return option?.icon ?? 'dots';
  }

  onToggle(serviceId: string, checked: boolean): void {
    // Los servicios de catálogo SSFF bloqueado no se seleccionan para OS.
    const service = this.servicios.find((s) => s.id === serviceId);
    if (!service || this.isServiceBlockedForOs(service)) {
      return;
    }
    this.toggleService.emit({ serviceId, checked });
    // Un servicio TICKET es un "seleccionar todos": su checkbox marca o
    // desmarca a la vez todos los tickets disponibles del servicio.
    if (this.isTicketService(service)) {
      for (const ticket of this.availableTicketsOf(service)) {
        if (ticket.id) {
          this.ticketToggle.emit({ serviceId, ticketId: ticket.id, checked });
        }
      }
      if (checked) {
        this.expandedServiceIds.add(service.id);
        this.cdr.markForCheck();
      }
    }
  }

  /** Tickets seleccionables de un servicio TICKET: sin OS y en estado CREADO. */
  private availableTicketsOf(service: MassOsItineraryService): MassOsTicket[] {
    if (!(service?.total != null && Number(service.total) > 0)) {
      return [];
    }
    return (service.tickets ?? []).filter(
      (ticket) =>
        !ticket?.order?.orderId &&
        String(ticket?.estadoTicket ?? '').toUpperCase() === 'CREADO'
    );
  }

  /* ================= Stickers de origen (SSFF / ADD / UPGRADE) ================= */

  /** Origen del servicio frente al catálogo SSFF. */
  serviceOrigin(service: MassOsItineraryService): MassOsServiceOrigin {
    return service.displayTypeService;
  }

  /** Texto corto del sticker (SSFF / ADD / UPGRADE). */
  originLabel(service: MassOsItineraryService): string {
    const origin = this.serviceOrigin(service);
    return origin ? MASS_OS_SERVICE_ORIGIN_LABEL[origin] : '';
  }

  /** Tooltip del sticker. */
  originHint(service: MassOsItineraryService): string {
    return massOsServiceOriginHintOf(service.displayTypeService);
  }

  /** Catálogo SSFF con producto → NO seleccionable para OS. */
  isServiceBlockedForOs(service: MassOsItineraryService): boolean {
    return service.displayTypeService === 'ssff';
  }

  /* ================= Expand de TICKET ================= */

  isTicketService(service: MassOsItineraryService): boolean {
    return String(service?.tipoServicio ?? '').toUpperCase() === 'TICKET';
  }

  isServiceExpanded(service: MassOsItineraryService): boolean {
    return this.expandedServiceIds.has(service.id);
  }

  toggleServiceExpand(service: MassOsItineraryService): void {
    if (this.expandedServiceIds.has(service.id)) {
      this.expandedServiceIds.delete(service.id);
    } else {
      this.expandedServiceIds.add(service.id);
    }
    this.cdr.markForCheck();
  }
}
